import React, { useState, useCallback } from 'react';

/**
 * DiaryFAB - Floating action button for quick-adding bookings, events, and leave.
 * Google Calendar-style FAB in the bottom-right corner.
 * `showImport` adds "Import anything" (AI onboarding / import layer, re-entrant
 * after setup — ONBOARDING-IMPORT-PLAN.md §4.5).
 */
function DiaryFAB({ onAction, showImport = false }) {
  const [open, setOpen] = useState(false);

  const handleToggle = useCallback(() => {
    setOpen(prev => {
      if (!prev) {
        // Close any open slot popups by simulating outside click
        document.body.click();
      }
      return !prev;
    });
  }, []);

  const handleSelect = useCallback((type) => {
    setOpen(false);
    if (onAction) onAction(type);
  }, [onAction]);

  return (
    <>
      {/* Backdrop */}
      {open && <div className="dv2-fab-backdrop" onClick={() => setOpen(false)} />}

      {/* Menu popup */}
      {open && (
        <div className="dv2-fab-menu">
          <button className="dv2-fab-item" onClick={() => handleSelect('booking')}>
            <span className="dv2-fab-item-icon">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <rect x="3" y="4" width="14" height="13" rx="2" stroke="currentColor" strokeWidth="1.4"/>
                <path d="M3 8H17" stroke="currentColor" strokeWidth="1.4"/>
                <path d="M7 2V5M13 2V5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
                <path d="M10 11V14M8.5 12.5H11.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
              </svg>
            </span>
            <span>Booking</span>
          </button>
          <button className="dv2-fab-item" onClick={() => handleSelect('event')}>
            <span className="dv2-fab-item-icon">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="1.4"/>
                <path d="M10 6.5V10L12.5 11.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
              </svg>
            </span>
            <span>Event</span>
          </button>
          <button className="dv2-fab-item" onClick={() => handleSelect('leave')}>
            <span className="dv2-fab-item-icon">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="8" r="4" stroke="currentColor" strokeWidth="1.4"/>
                <path d="M10 4V2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
                <path d="M14 8H16" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
                <path d="M4 8H6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
                <path d="M12.8 5.2L14.2 3.8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
                <path d="M5.8 3.8L7.2 5.2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
                <path d="M3 14H17" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
                <path d="M5 17H15" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
              </svg>
            </span>
            <span>Leave</span>
          </button>
          {showImport && (
            <button className="dv2-fab-item" onClick={() => handleSelect('import')}>
              <span className="dv2-fab-item-icon">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M10 13V4M6.5 7.5L10 4l3.5 3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M4 13v3h12v-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
              <span>Import anything</span>
            </button>
          )}
        </div>
      )}

      {/* FAB button */}
      <button
        className={`dv2-fab ${open ? 'dv2-fab--open' : ''}`}
        onClick={handleToggle}
        aria-label="Add new"
      >
        <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
          <path d="M11 4V18M4 11H18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"/>
        </svg>
      </button>
    </>
  );
}

export default DiaryFAB;
